import { useEffect, useState } from 'react';
import { Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { mktApi, errMsg } from './marketingApi';

const EMPTY = {
  name: '', code: '', kana: '', short_name: '', zip: '', address: '',
  contact: '', email: '', phone: '', fax: '', honorific: '御中',
  closing_day: '', payment_terms: '', invoice_reg_no: '',
  shipping_addresses: [], status: 'active', notes: '',
};

export default function CustomerFormDialog({ open, onOpenChange, customer = null, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(customer ? {
      ...EMPTY, ...customer,
      closing_day: customer.closing_day ?? '',
      shipping_addresses: customer.shipping_addresses || [],
    } : EMPTY);
  }, [open, customer]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setShip = (idx, k, v) => setF((p) => ({ ...p, shipping_addresses: p.shipping_addresses.map((s, i) => (i === idx ? { ...s, [k]: v } : s)) }));

  const save = async () => {
    if (!f.name.trim()) { toast.error('客先名を入力してください'); return; }
    setSaving(true);
    try {
      const body = {
        ...f,
        closing_day: f.closing_day === '' ? null : Number(f.closing_day),
        shipping_addresses: (f.shipping_addresses || []).filter((s) => (s.label || s.address)),
      };
      delete body.id; delete body.created_at; delete body.updated_at; delete body.created_by;
      if (customer) await mktApi.updateCustomer(customer.id, body);
      else await mktApi.createCustomer(body);
      toast.success(customer ? '客先を更新しました' : '客先を登録しました');
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const Field = ({ label, k, ph = '', type = 'text', span = '' }) => (
    <div className={span} key={k}>
      <label className="text-xs font-medium text-zinc-600">{label}</label>
      <Input type={type} value={f[k] ?? ''} onChange={(e) => set(k, e.target.value)} placeholder={ph} className="mt-1 h-9" data-testid={`mkt-customer-field-${k}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mkt-customer-form-dialog">
        <DialogHeader><DialogTitle>{customer ? `客先を編集 — ${customer.name}` : '新規客先'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-zinc-600">客先名 <span className="text-red-500">*</span></label>
              <Input value={f.name} onChange={(e) => set('name', e.target.value)} className="mt-1 h-9" data-testid="mkt-customer-field-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">敬称</label>
              <Select value={f.honorific} onValueChange={(v) => set('honorific', v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="mkt-customer-field-honorific"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="御中">御中</SelectItem><SelectItem value="様">様</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Field({ label: 'かな', k: 'kana' })}
            {Field({ label: '客先コード', k: 'code' })}
            {Field({ label: '略称', k: 'short_name' })}
            <div>
              <label className="text-xs font-medium text-zinc-600">状態</label>
              <Select value={f.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="mkt-customer-field-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">有効</SelectItem><SelectItem value="inactive">無効</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Field({ label: '郵便番号', k: 'zip', ph: '123-4567' })}
            {Field({ label: '住所', k: 'address', span: 'sm:col-span-2' })}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Field({ label: '担当者', k: 'contact' })}
            {Field({ label: 'Email', k: 'email' })}
            {Field({ label: 'TEL', k: 'phone' })}
            {Field({ label: 'FAX', k: 'fax' })}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-600">締め日（99=末日）</label>
              <Input type="number" min="1" max="99" value={f.closing_day} onChange={(e) => set('closing_day', e.target.value)} className="mt-1 h-9" data-testid="mkt-customer-field-closing_day" />
            </div>
            {Field({ label: '支払条件', k: 'payment_terms', ph: '例）翌月末' })}
            {Field({ label: 'インボイス登録番号', k: 'invoice_reg_no', ph: 'T1234567890123', span: 'col-span-2' })}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-zinc-600">出荷先住所（任意）</label>
              <Button type="button" variant="outline" size="sm" className="h-7"
                onClick={() => set('shipping_addresses', [...(f.shipping_addresses || []), { label: '', zip: '', address: '', contact: '', phone: '' }])}
                data-testid="mkt-customer-add-shipping-btn">
                <Plus size={12} className="mr-1" /> 追加
              </Button>
            </div>
            <div className="space-y-2">
              {(f.shipping_addresses || []).map((s, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 rounded-lg border border-zinc-200 bg-zinc-50/50 p-2" data-testid={`mkt-customer-shipping-${idx}`}>
                  <Input value={s.label || ''} onChange={(e) => setShip(idx, 'label', e.target.value)} placeholder="名称（本社/工場等）" className="col-span-3 h-8 text-xs" />
                  <Input value={s.zip || ''} onChange={(e) => setShip(idx, 'zip', e.target.value)} placeholder="〒" className="col-span-2 h-8 text-xs" />
                  <Input value={s.address || ''} onChange={(e) => setShip(idx, 'address', e.target.value)} placeholder="住所" className="col-span-4 h-8 text-xs" />
                  <Input value={s.phone || ''} onChange={(e) => setShip(idx, 'phone', e.target.value)} placeholder="TEL" className="col-span-2 h-8 text-xs" />
                  <button type="button" onClick={() => set('shipping_addresses', f.shipping_addresses.filter((_, i) => i !== idx))}
                    className="col-span-1 text-zinc-400 hover:text-red-500"><Trash size={15} /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-600">備考</label>
            <Textarea value={f.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={2} className="mt-1" data-testid="mkt-customer-field-notes" />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mkt-customer-form-cancel-btn">キャンセル</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-customer-form-save-btn">
              {saving ? '保存中…' : (customer ? '更新する' : '登録する')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
